import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { DataCard, Label, Popover } from '@brighterion/br-ui';
import { TFunction } from 'i18next';
import { NumberService } from 'Services';
import { shouldShowMerchants, shouldShowExecutionOrder } from 'Selectors';
import { getEmptyStringReplacementText, handleKeyEvent, handleOnClickEvent } from 'Utils';
import useGetMerchantOverrides from 'Hooks/query-hooks/merchants/useGetMerchantOverrides';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetTenantGroupOverrideRules from 'Hooks/query-hooks/tenant/useGetTenantGroupOverrideRules';
import { FormikFieldString, FormikRuleToggle } from 'Components/FormikComponents';
import RevisionHistoryModal from 'Components/RevisionHistoryModal';
import DashOrEllipText from 'Components/DashOrEllipText';
import AddRuleOverview from 'Components/AddRulePage/AddRuleOverview';
import Overview from 'Components/Overview';
import { Levels, RULE_INPUT_PARAMETER_NAMES } from 'Common/enums';

import styles from './EditRuleInfoOverview.css';

type OverrideSubject = 'merchant' | 'tenantGroup';

type Props = {
    currentGlobalRule: Rule;
    currentOverrideRule?: Rule;
    rulesType: RulesType;
    isSubmitting: boolean;
    openModal: boolean;
    setOpenModal: (open: boolean) => void;
    listData?: ListApiGetResponse;
    subjectIdentifier: string | undefined;
};

const genGlobalRuleOverviewTop = (apiData: Rule, t: TFunction, showExecutionOrder: boolean) => [
    {
        id: 'ruleId',
        label: t('Rule ID'),
        data: (
            <Popover
                position='top-end'
                size='small'
                showArrow
                showOnHover
                popperClassName={styles.popperStyle}
                contentClassName={styles.popOverContentStyle}
                arrowClassName={styles.popperArrowStyle}
                content={t('editDefaultProhibited')}
            >
                <FormikFieldString
                    label={t('Rule ID')}
                    name='ruleId'
                    className={styles.ruleField}
                    value={apiData?.id}
                    disabled
                />
            </Popover>
        )
    },
    {
        id: 'ruleGroup',
        label: t('Rule Group'),
        data: (
            // hence users won't be able to edit rule group, just display FormikFieldString
            // so that we don't make a call to /groups
            <Popover
                position='top-end'
                size='small'
                showArrow
                showOnHover
                popperClassName={styles.popperStyle}
                contentClassName={styles.popOverContentStyle}
                arrowClassName={styles.popperArrowStyle}
                content={t('editDefaultProhibited')}
            >
                <FormikFieldString
                    label={t('Rule Group')}
                    name='ruleGroup'
                    className={styles.groupField}
                    value={apiData?.ruleGroup}
                    disabled
                />
            </Popover>
        )
    },
    ...(showExecutionOrder
        ? [
              {
                  id: RULE_INPUT_PARAMETER_NAMES.PRIORITY,
                  label: t('executionOrder'),
                  data: (
                      <>
                          <Popover
                              position='top-end'
                              size='small'
                              showArrow
                              showOnHover
                              popperClassName={styles.popperStyle}
                              contentClassName={styles.popOverContentStyle}
                              arrowClassName={styles.popperArrowStyle}
                              content={t('executionOrderToolTipForDefault')}
                          >
                              <FormikFieldString
                                  label={t('executionOrder')}
                                  name={RULE_INPUT_PARAMETER_NAMES.PRIORITY}
                                  className={styles.executionOrder}
                                  disabled
                                  type='text'
                                  value={apiData?.priority}
                                  min={1}
                              />
                          </Popover>
                      </>
                  )
              }
          ]
        : [])
];

const genGlobalRuleOverviewBottom = (apiData: Rule, t: TFunction) => {
    return [
        {
            id: 'ruleDescription',
            label: t('Rule Description'),
            data: (
                <Popover
                    position='top'
                    size='small'
                    showArrow
                    showOnHover
                    popperClassName={styles.popperStyle}
                    contentClassName={styles.popOverContentStyle}
                    arrowClassName={styles.popperArrowStyle}
                    content={t('editDefaultProhibited')}
                >
                    <FormikFieldString
                        label={t('Rule Description')}
                        name='ruleDescription'
                        className={styles.descriptionField}
                        value={apiData?._formatted?.inputDescription}
                        disabled
                    />
                </Popover>
            )
        }
    ];
};

const genGlobalRuleOverviewMiddle = (
    apiData: Rule,
    t: TFunction,
    isSubmitting: boolean,
    overrideSubjectIds: string[],
    overrideSubject: OverrideSubject
) => {
    const subjectOverridesCopiedMessage = 'Copied';
    let subjectOverridesLabel = '';
    let subjectOverridesNumberMessage = '';
    switch (overrideSubject) {
        case 'merchant':
            subjectOverridesLabel = 'Merchant Overrides';
            subjectOverridesNumberMessage = 'Number of Merchants';
            break;
        case 'tenantGroup':
            subjectOverridesLabel = 'Tenant Group Overrides';
            subjectOverridesNumberMessage = 'Number of Tenant Groups';
            break;
        default:
            break;
    }
    return [
        {
            id: 'status',
            label: t('Status'),
            data: (
                <div className={styles.statusField}>
                    <Label label={t('Status')} id='status' />
                    <FormikRuleToggle name='active' text={t('Rule is')} disabled={isSubmitting} />
                </div>
            )
        },
        {
            id: 'SubjectOverrides',
            label: subjectOverridesLabel,
            data: (
                <div className={styles.subjectOverridesContainer}>
                    <Label label={t(subjectOverridesLabel)} id='SubjectOverrides' />
                    {overrideSubjectIds.length > 0 ? (
                        <Popover
                            showCopy
                            showOnHover
                            showArrow
                            theme='light'
                            size='small'
                            content={overrideSubjectIds.join(', ')}
                            copiedMessage={t(subjectOverridesCopiedMessage)}
                            popperClassName={styles.subjectOverridesPopper}
                        >
                            <span id='copiedSubjects' className={styles.subjectOverridesText}>
                                {t(subjectOverridesNumberMessage, { count: overrideSubjectIds.length })}
                            </span>
                        </Popover>
                    ) : (
                        <Label label={t(subjectOverridesNumberMessage, { count: 0 })} id='noOverrides' />
                    )}
                </div>
            )
        },
        {
            id: 'lastUpdated',
            label: t('Last Updated'),
            data: (
                <div className={styles.lastUpdatedField}>
                    <Label label={t('Last Updated')} id='lastUpdated' className={styles.lastUpdatedLabel} />
                    <div className={styles.lastUpdatedText}>
                        {apiData?._formatted?.createdAt &&
                            apiData?.createdBy &&
                            getEmptyStringReplacementText(
                                `${apiData?._formatted?.createdAt} by ${apiData?._formatted?.createdBy}`
                            )}
                    </div>
                </div>
            )
        }
    ];
};

const genGlobalRuleOverviewNoGroup = (apiData: Rule, t: TFunction, showExecutionOrder: boolean) => [
    {
        id: 'ruleId',
        label: t('Rule ID'),
        data: apiData?.id
    },
    {
        id: 'ruleDescription',
        label: t('Rule Description'),
        data: apiData?._formatted?.inputDescription
    },
    ...(showExecutionOrder
        ? [
              {
                  id: 'executionOrder',
                  label: t('executionOrder'),
                  data: apiData.nonTemplated ? apiData?.priority : t('N/A')
              }
          ]
        : []),
    {
        id: 'status',
        label: t('Status'),
        data: apiData?.parameters?.active ? t('Global Rule is ACTIVE') : t('Global Rule is INACTIVE')
    },
    {
        id: 'lastUpdated',
        label: t('Last Updated'),
        data:
            apiData?._formatted?.createdAt &&
            apiData?.createdBy &&
            getEmptyStringReplacementText(`${apiData?._formatted?.createdAt} by ${apiData?._formatted?.createdBy}`)
    }
];

const genMerchantRuleOverview = (apiData: Rule, t: TFunction, isSubmitting: boolean, showExecutionOrder: boolean) => [
    {
        id: 'ruleId',
        label: t('Rule ID'),
        data: apiData?.id
    },
    {
        id: 'ruleDescription',
        label: t('Override Description'),
        data: apiData?._formatted?.inputDescription
    },
    ...(showExecutionOrder
        ? [
              {
                  id: 'executionOrder',
                  label: t('executionOrder'),
                  data: apiData.nonTemplated ? apiData?.priority : t('N/A')
              }
          ]
        : []),
    {
        id: 'status',
        label: t('Status'),
        data: (
            <div className={styles.MerchantStatusField}>
                <FormikRuleToggle name='active' text={t('Rule is')} disabled={isSubmitting} />
            </div>
        )
    },
    {
        id: 'lastUpdated',
        label: t('Last Updated'),
        data:
            apiData?._formatted?.createdAt &&
            apiData?.createdBy &&
            getEmptyStringReplacementText(`${apiData?._formatted?.createdAt} by ${apiData?._formatted?.createdBy}`)
    }
];

const genDataCards = (data: React.ComponentProps<typeof DataCard>[], subheaderText: string, isBolded: boolean) => {
    return (
        <>
            <div className={styles.subheader}>{subheaderText}</div>
            <div className={styles.left}>
                {data?.map((o) => {
                    const formattedData =
                        typeof o?.data === 'number' ? NumberService.getLocaleNumber(o?.data) : o?.data;
                    const text = <DashOrEllipText rawValue={formattedData as string} bold={isBolded} />;
                    return (
                        <DataCard
                            dataBoldFont
                            dataTestId='merchant-edit-rule-info-overview-card'
                            className={clsx(styles.leftCard, o?.className)}
                            key={o?.label}
                            data={text}
                            label={o?.label}
                        />
                    );
                })}
            </div>
        </>
    );
};

const EditRuleInfoOverview = ({
    currentGlobalRule,
    openModal,
    setOpenModal,
    currentOverrideRule,
    rulesType,
    isSubmitting,
    listData,
    subjectIdentifier
}: Props) => {
    const { t } = useTranslation();
    const showExecutionOrder = shouldShowExecutionOrder();
    const { data: merchantOverrides } = useGetMerchantOverrides();
    const { data: landlordInfo } = useGetLandlordInfo();
    const isLandlord = !!landlordInfo?.isLandlord;
    const overrideSubject: OverrideSubject = isLandlord ? 'tenantGroup' : 'merchant';
    const { data: tenantGroupOverrides } = useGetTenantGroupOverrideRules(overrideSubject === 'tenantGroup');
    const overrideTenantGroupIds =
        tenantGroupOverrides && currentGlobalRule.id ? tenantGroupOverrides[currentGlobalRule.id] || [] : [];
    const overrideMerchantIds =
        merchantOverrides && currentGlobalRule.id ? merchantOverrides[currentGlobalRule.id] || [] : [];
    const overrideSubjectIds = isLandlord ? overrideTenantGroupIds : overrideMerchantIds;
    let bottomData = genGlobalRuleOverviewBottom(currentGlobalRule, t);
    const middleData = genGlobalRuleOverviewMiddle(
        currentGlobalRule,
        t,
        isSubmitting,
        overrideSubjectIds,
        overrideSubject
    );
    if (!shouldShowMerchants() && overrideSubject === 'merchant') {
        bottomData = bottomData.filter((obj) => obj.id !== 'SubjectOverrides');
    }

    let level;
    switch (rulesType) {
        case 'globalRules':
        case 'tenantOverrides':
            level = Levels.ACQUIRER;
            break;
        case 'merchantOverrides':
            level = Levels.MERCHANT;
            break;
        case 'tenantGroupOverrides':
            level = Levels.TENANT_GROUP;
            break;
        default:
            level = Levels.ACQUIRER;
            break;
    }
    return (
        <>
            {rulesType === 'globalRules' ? (
                <AddRuleOverview
                    dataTestId='global-edit-rule-info-overview'
                    leftTitle='Rule Information'
                    // Rule ID, Rule Group, Status, Last Updated
                    topData={genGlobalRuleOverviewTop(currentGlobalRule!, t, showExecutionOrder)}
                    rightTitle={
                        <div
                            role='button'
                            tabIndex={0}
                            onKeyDown={handleKeyEvent(() => setOpenModal(true))}
                            onClick={handleOnClickEvent(() => setOpenModal(true))}
                        >
                            View Revision History
                        </div>
                    }
                    // Description, overrides
                    bottomData={bottomData}
                    middleData={middleData}
                />
            ) : (
                <Overview
                    dataTestId='merchant-edit-rule-info-overview'
                    leftTitle='Override Information'
                    rightTitle={
                        <div
                            role='button'
                            tabIndex={0}
                            onKeyDown={handleKeyEvent(() => setOpenModal(true))}
                            onClick={handleOnClickEvent(() => setOpenModal(true))}
                        >
                            View Revision History
                        </div>
                    }
                    boldedText={false}
                >
                    <div className={styles.overviewContentContainer}>
                        {genDataCards(
                            genGlobalRuleOverviewNoGroup(currentGlobalRule!, t, showExecutionOrder),
                            t('Global Rule'),
                            false
                        )}
                        <div className={styles.divider} />
                        {genDataCards(
                            genMerchantRuleOverview(currentOverrideRule!, t, isSubmitting, showExecutionOrder),
                            t(rulesType === 'merchantOverrides' ? 'Merchant OVERRIDE' : 'Tenant OVERRIDE'),
                            true
                        )}
                    </div>
                </Overview>
            )}
            <RevisionHistoryModal
                title={rulesType === 'globalRules' ? t('Rule Revision History') : t('Override Revision History')}
                ruleId={rulesType === 'globalRules' ? currentGlobalRule.id : currentOverrideRule!.id}
                level={level}
                subjectIdentifier={subjectIdentifier}
                listData={listData?.result}
                openModal={openModal}
                setOpenModal={setOpenModal}
            />
        </>
    );
};

export default React.memo(EditRuleInfoOverview);
